import { createRouter, createWebHistory } from 'vue-router'
import { sectionForRoute, canAccessSection } from '../utils/permissions'
import HomeView from '../views/HomeView.vue'
import SignupView from '../views/SignupView.vue'
import LoginView from '../views/LoginView.vue'
import OtpView from '../views/OtpView.vue'
import TwoFactorSetupView from '../views/TwoFactorSetupView.vue'
import ProfilView from '../views/ProfilView.vue'
import StatistiquesView from '../views/StatistiquesView.vue'
import AdminDashboard from '../views/AdminDashboard.vue'
import UsersView from '../views/UsersView.vue'
import AuditView from '../views/AuditView.vue'
import CourrierTimelineView from '../views/CourrierTimelineView.vue'
import UserActivityView from '../views/UserActivityView.vue'
import RequestCorrelationView from '../views/RequestCorrelationView.vue'
import AuditArchivesView from '../views/AuditArchivesView.vue'
import DirectionsListView from '../views/DirectionsListView.vue'
import DepartementsListView from '../views/DepartementsListView.vue'
import ServicesListView from '../views/ServicesListView.vue'
import TypesDocumentView from '../views/TypesDocumentView.vue'
import WorkflowTemplatesView from '../views/WorkflowTemplatesView.vue'
import WorkflowTemplateBuilderView from '../views/WorkflowTemplateBuilderView.vue'
import SuiviCourriersView from '../views/SuiviCourriersView.vue'
import RapportJournalierView from '../views/RapportJournalierView.vue'
import LogsView from '../views/LogsView.vue'
import FeedbackView from '../views/FeedbackView.vue'
import NotificationsView from '../views/NotificationsView.vue'
import RolesView from '../views/RolesView.vue'
import { useAuthStore } from '../store/auth'

const routes = [
  { path: '/', name: 'login', component: LoginView },
  { path: '/otp', name: 'otp', component: OtpView },
  { path: '/setup-2fa', name: 'setup-2fa', component: TwoFactorSetupView },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: AdminDashboard,
    meta: { requiresAdmin: true }
  },
  {
    path: '/statistiques',
    name: 'statistiques',
    component: StatistiquesView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/profil',
    name: 'profil',
    component: ProfilView,
    meta: { requiresAdmin: true }
  },
  { 
    path: '/users', 
    name: 'users', 
    component: UsersView,
    meta: { requiresAdmin: true }
  },
  { 
    path: '/organisation', 
    name: 'organisation', 
    component: DirectionsListView,
    meta: { requiresAdmin: true }
  },
  { 
    path: '/organisation/directions/:directionId/departements', 
    name: 'departements', 
    component: DepartementsListView,
    meta: { requiresAdmin: true }
  },
  { 
    path: '/organisation/directions/:directionId/departements/:departementId/services', 
    name: 'services', 
    component: ServicesListView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/types-document',
    name: 'types-document',
    component: TypesDocumentView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/workflow-templates',
    name: 'workflow-templates',
    component: WorkflowTemplatesView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/workflow-templates/:id',
    name: 'workflow-template-builder',
    component: WorkflowTemplateBuilderView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/suivi-courriers',
    name: 'suivi-courriers',
    component: SuiviCourriersView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/rapport-journalier',
    name: 'rapport-journalier',
    component: RapportJournalierView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/logs',
    name: 'logs',
    component: LogsView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/audit',
    name: 'audit',
    component: AuditView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/audit/courrier/:id',
    name: 'audit-courrier-timeline',
    component: CourrierTimelineView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/audit/user/:id',
    name: 'audit-user-activity',
    component: UserActivityView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/audit/request/:uuid',
    name: 'audit-request-correlation',
    component: RequestCorrelationView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/audit/archives',
    name: 'audit-archives',
    component: AuditArchivesView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: FeedbackView,
    meta: { requiresAdmin: true }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsView,
    meta: { requiresAdmin: true }
  },
  // Ancienne adresse de la page « Alertes sécurité », désormais un onglet de Notifications.
  { path: '/alertes-securite', redirect: { path: '/notifications', query: { onglet: 'securite' } } },
  {
    path: '/roles',
    name: 'roles',
    component: RolesView,
    meta: { requiresAdmin: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Guard de navigation simple
// La vraie sécurité est dans le backend via AdminOnlyMiddleware
router.beforeEach(async (to: any, from: any, next: any) => {
  const authStore = useAuthStore()
  
  // Attendre la fin du chargement de l'état d'authentification si nécessaire
  if (authStore.loading) {
    await new Promise<void>(resolve => {
      const unwatch = authStore.$subscribe(() => {
        if (!authStore.loading) {
          unwatch()
          resolve()
        }
      })
    })
  }
  
  const isAuthenticated = authStore.isAuthenticated
  const roleCode = authStore.user?.role?.code
  const perms = authStore.user?.backoffice_permissions
  const canAccessBackoffice =
    roleCode === 'ADMIN' || roleCode === 'DIRECTEUR' || roleCode === 'AMBASSADEUR' ||
    !!(perms && perms.sections && perms.sections.length > 0)
  const isLoginPage = to.name === 'login' || to.name === 'otp' || to.name === 'setup-2fa'
  const requiresAdmin = to.meta.requiresAdmin
  
  // Si l'utilisateur est connecté et essaie d'accéder à la page de login
  if (isAuthenticated && isLoginPage) {
    // Rediriger vers le dashboard
    next({ name: 'dashboard' })
    return
  }
  
  // Si la route nécessite l'authentification admin
  if (requiresAdmin) {
    // Si pas authentifié, rediriger vers login
    if (!isAuthenticated) {
      next({ name: 'login' })
      return
    }
    
    // Si authentifié mais sans rôle autorisé, déconnecter et rediriger
    if (!canAccessBackoffice) {
      alert('Accès refusé. Seuls les profils ADMIN, DIRECTEUR ou AMBASSADEUR peuvent accéder au backoffice.')
      await authStore.logout()
      next({ name: 'login' })
      return
    }
    
    // Profil restreint : si la section de la route n'est pas autorisée,
    // rediriger vers le tableau de bord (au lieu de bloquer/déconnecter).
    const section = sectionForRoute(to.name)
    if (section && !canAccessSection(authStore.canSection, section)) {
      next({ name: 'dashboard' })
      return
    }

    // Utilisateur authentifié avec accès autorisé
    next()
    return
  }
  
  // Pour toutes les autres routes (login, otp), autoriser l'accès
  next()
})

export default router
